import React, { useState, useContext, useMemo } from 'react';
import { Check, Edit2, Trash2, Plus } from 'lucide-react';
import { AuthContext } from 'components/context/AuthContext';
import { brandsApi, usersApi } from 'api';
import { toast } from 'react-hot-toast';
import CreateBrandModal from 'components/modals/CreateBrandModal';
import EditBrandModal from 'components/modals/EditBrandModal';
import { GoogleIcon, MetaIcon } from 'components';
import MobileBottomSheet from 'components/ui/MobileBottomSheet';

/**
 * MobileBrandSelectorSheet
 * Mobile-optimized brand switcher for touch devices
 * Displays as a bottom sheet with large touch-friendly brand cards
 */
const MobileBrandSelectorSheet = ({ isOpen, onClose }) => {
  const { auth, updateContextUser } = useContext(AuthContext);
  const [changing, setChanging] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const brands = useMemo(() => auth.user?.brands ?? [], [auth.user?.brands]);
  const activeBrandId = auth.user?.active_brand_id;

  // La marca activa siempre aparece arriba
  const sortedBrands = useMemo(() => {
    if (!activeBrandId) return brands;
    return [...brands].sort((a, b) => {
      if (a.id === activeBrandId) return -1;
      if (b.id === activeBrandId) return 1;
      return 0;
    });
  }, [brands, activeBrandId]);

  const handleBrandChange = async (brandId) => {
    if (brandId === auth.user?.active_brand_id) {
      onClose();
      return;
    }

    setChanging(true);
    try {
      const response = await brandsApi.setActiveBrand(brandId);
      updateContextUser(response.data);
      toast.success('Marca activa cambiada');
      onClose();
      window.location.reload();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setChanging(false);
    }
  };

  const handleCreateBrand = async (brandData) => {
    try {
      await brandsApi.createBrand(brandData);
      toast.success('Marca creada exitosamente');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al crear marca: ${errorMessage}`);
      throw error;
    }
  };

  const handleUpdateBrand = async (brandData) => {
    if (!editingBrand) return;
    try {
      await brandsApi.updateBrand(editingBrand.id, brandData);
      toast.success('Marca actualizada exitosamente');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      setEditingBrand(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al actualizar marca: ${errorMessage}`);
      throw error;
    }
  };

  const handleDeleteBrand = async (e, brand) => {
    e.stopPropagation();
    if (brand.id === auth.user?.active_brand_id) return;
    if (!window.confirm(`¿Eliminar la marca "${brand.name}"?`)) return;
    try {
      await brandsApi.deleteBrand(brand.id);
      toast.success('Marca eliminada');
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Error al eliminar marca: ${errorMessage}`);
    }
  };

  return (
    <>
      <MobileBottomSheet isOpen={isOpen} onClose={onClose} title="Cambiar Marca" darkMode={true}>
        <div className="flex flex-col gap-3 pb-4">
          {sortedBrands.map((brand) => {
            const isActive = brand.id === auth.user?.active_brand_id;
            return (
              <button
                key={brand.id}
                type="button"
                onClick={() => handleBrandChange(brand.id)}
                disabled={changing}
                className={`
                  w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-between
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${isActive
                    ? 'bg-blue-500/20 border-blue-500 text-white'
                    : 'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20'
                  }
                `}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Platform status indicators */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative">
                      <GoogleIcon width={18} height={18} className={brand.google_ads_connected ? 'opacity-100' : 'opacity-40'} />
                      <span
                        className={`absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-slate-900 ${
                          brand.google_ads_connected ? 'bg-emerald-400' : 'bg-red-400'
                        }`}
                      />
                    </div>
                    <div className="relative">
                      <MetaIcon width={18} height={18} className={brand.meta_connected ? 'opacity-100' : 'opacity-40'} />
                      <span
                        className={`absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-slate-900 ${
                          brand.meta_connected ? 'bg-emerald-400' : 'bg-red-400'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Brand name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{brand.name}</p>
                  </div>
                </div>

                {/* Actions and active indicator */}
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {!isActive && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingBrand(brand);
                      }}
                      className="p-2 hover:bg-white/20 rounded transition flex-shrink-0 active:scale-95"
                      title="Editar marca"
                    >
                      <Edit2 className="w-4 h-4 text-white/70" />
                    </button>
                  )}
                  {!isActive && (
                    <button
                      type="button"
                      onClick={(e) => handleDeleteBrand(e, brand)}
                      className="p-2 hover:bg-red-500/30 rounded transition flex-shrink-0 active:scale-95"
                      title="Eliminar marca"
                    >
                      <Trash2 className="w-4 h-4 text-red-300" />
                    </button>
                  )}
                  {isActive && (
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}

          {/* Create new brand button */}
          <button
            type="button"
            onClick={() => {
              setIsCreateModalOpen(true);
            }}
            className="w-full p-4 rounded-lg border-2 border-dashed border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white transition-all duration-200 flex items-center gap-3 active:scale-95"
          >
            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/30 border border-blue-400/50 flex-shrink-0">
              <Plus size={14} className="text-blue-200" />
            </div>
            <span className="font-medium">Nueva Marca</span>
          </button>
        </div>
      </MobileBottomSheet>

      <CreateBrandModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateBrand={handleCreateBrand}
      />

      <EditBrandModal
        isOpen={!!editingBrand}
        onClose={() => setEditingBrand(null)}
        brand={editingBrand}
        onUpdateBrand={handleUpdateBrand}
      />
    </>
  );
};

export default MobileBrandSelectorSheet;
